import { IChipList } from '@nocode/types';
import { get, isEmpty } from 'lodash';
import React from 'react';
import { Image, View } from 'react-native';
import { Chip, Paragraph } from 'react-native-paper';
import { defaultURL } from '../../../../utils/common.constant';
import { checkFont } from '../../func';
import {
  getActions,
  getImageCardCover,
  getItemListClick,
  getValueBinding,
  isCheckColor,
} from '../../../shared';

type Props = {
  attrs: IChipList;
  active: boolean;
  index: number;
  item: any;
  onPress?: any;
  finalStyle?: any;
};

const ChipAvatar = ({
  attrs,
  item,
  finalStyle,
}: Pick<Props, 'attrs' | 'item' | 'finalStyle'>) => {
  const { cardCover } = attrs.attributes;
  const itemSize = attrs.attributes.itemSize || 32;
  const itemMargin = finalStyle?.gap || attrs.attributes.gap || 0;

  const placeholderImageUrl = get(
    attrs,
    'attributes.cardCover.image.binding.options.placeholderImage',
    null
  );
  const placeholderImageEnabled = get(
    attrs,
    'attributes.cardCover.image.binding.options.placeholderImageEnabled',
    false
  );

  const imageType = get(cardCover, 'image.imageType');
  const placeholderImage =
    placeholderImageEnabled && imageType !== 'uploaded'
      ? placeholderImageUrl
      : null;

  const imageURL = getImageCardCover(item, cardCover);
  const placeholderUrl = placeholderImageEnabled
    ? placeholderImage
    : defaultURL;
  const cardCoverImage = !isEmpty(imageURL) ? imageURL : placeholderUrl;

  const avatarStyle = {
    display: 'flex' as const,
    alignItems: 'center' as const,
    height: itemSize * 0.6,
    width: itemSize * 0.6,
    marginTop: itemMargin * 0.5,
    marginBottom: itemMargin * 0.5,
    borderColor:
      finalStyle?.borderColor || attrs.attributes.cardCover?.borderColor,
    borderWidth:
      finalStyle?.borderWidth || attrs.attributes.cardCover?.borderWidth,
    borderRadius:
      finalStyle?.borderRadius || attrs.attributes.cardCover?.borderRadius,
    borderStyle:
      (finalStyle?.borderStyle || attrs.attributes.cardCover?.borderStyle) !==
      'none'
        ? finalStyle?.borderStyle || attrs.attributes.cardCover?.borderStyle
        : undefined,
  };

  return (
    <View style={avatarStyle}>
      <Image
        style={{
          height: '100%',
          width: '100%',
          borderRadius: finalStyle?.borderRadius || cardCover?.borderRadius,
        }}
        defaultSource={{
          uri: placeholderUrl,
        }}
        source={{
          uri: cardCoverImage,
        }}
      />
    </View>
  );
};

const ChipItem = ({
  attrs,
  index,
  item,
  onPress,
  active,
  finalStyle,
}: Props) => {
  const {
    closeIcon = { enabled: false },
    title = { enabled: false },
    cardCover = { enabled: false },
  } = attrs.attributes;

  const bindingValue = getValueBinding('', item, attrs);
  const itemSize = attrs.attributes.itemSize || 32;
  const itemMargin = finalStyle?.gap || attrs.attributes.gap || 0;

  // Style processing similar to CardItem but keeping original color logic
  const backgroundColor = isCheckColor(
    finalStyle?.backgroundColor || attrs.attributes.backgroundColor
  )
    ? finalStyle?.backgroundColor || attrs.attributes.backgroundColor
    : '#ffffff';

  const selectedColor = isCheckColor(attrs.attributes.selectedColor)
    ? attrs.attributes.selectedColor
    : '#4259AC';

  const color = isCheckColor(finalStyle?.color || attrs.attributes.color)
    ? finalStyle?.color || attrs.attributes.color
    : '#000000';

  const selectedFontColor = isCheckColor(attrs.attributes.selectedFontColor)
    ? attrs.attributes.selectedFontColor
    : '#ffffff';

  // Enhanced chip style with finalStyle integration
  const chipStyle = {
    height: itemSize * 0.8,
    marginHorizontal: itemMargin,
    display: 'flex' as const,
    alignItems: 'center' as const,
    flexDirection: 'row' as const,
    marginTop: itemSize * 0.1,
    marginBottom: itemSize * 0.1,
    shadowColor: finalStyle?.shadowColor || attrs.attributes.shadow?.color,
    shadowOffset: finalStyle?.shadowOffset || {
      width: attrs.attributes.shadow?.x,
      height: attrs.attributes.shadow?.y,
    },
    shadowOpacity: finalStyle?.shadowOpacity || attrs.attributes.shadow?.blur,
    shadowRadius: finalStyle?.shadowRadius || attrs.attributes.shadow?.size,
    elevation: finalStyle?.elevation || 5,
    opacity: finalStyle?.opacity || attrs.attributes.opacity || 1,
    backgroundColor: active ? selectedColor : backgroundColor,
    borderColor:
      finalStyle?.borderColor || attrs.attributes.borderColor || '#e0e0e0',
    borderRadius: finalStyle?.borderRadius || attrs.attributes.borderRadius,
    borderWidth: finalStyle?.borderWidth || attrs.attributes.borderWidth,
    borderStyle:
      (finalStyle?.borderStyle || attrs.attributes.borderStyle) !== 'none'
        ? finalStyle?.borderStyle || attrs.attributes.borderStyle
        : undefined,
  };

  const textStyle = {
    fontFamily: checkFont(
      finalStyle?.fontFamily || attrs.attributes.fontFamily
    ),
    fontSize: finalStyle?.fontSize || attrs.attributes.fontSize,
    color: active ? selectedFontColor : color,
    fontWeight: finalStyle?.fontWeight || attrs.attributes.fontWeight,
  };

  return (
    <Chip
      {...(closeIcon.enabled && closeIcon.icon !== ''
        ? {
            closeIcon: closeIcon.icon,
            onClose: () => {
              onPress &&
                onPress(getActions(attrs, 'closeIcon'), {
                  record: item,
                  indexRecord: index,
                });
            },
            selectedColor: isCheckColor(attrs.attributes.iconColor)
              ? attrs.attributes.iconColor
              : '#000000',
          }
        : {})}
      onPress={() =>
        onPress &&
        onPress(getActions(attrs, 'onPress'), {
          record: item,
          indexRecord: index,
        })
      }
      style={chipStyle}
      textStyle={textStyle}
      avatar={
        cardCover.enabled ? (
          <ChipAvatar attrs={attrs} item={item} finalStyle={finalStyle} />
        ) : undefined
      }
    >
      {title.enabled && (
        <Paragraph style={textStyle}>
          {get(bindingValue, 'title.text')}
        </Paragraph>
      )}
    </Chip>
  );
};

export default ChipItem;
